import { Link, useSearchParams } from 'react-router';
import { ApiError, type components } from '@awj/api-client';
import { formatDate } from '@awj/i18n';
import { Button, Skeleton, useToast } from '@awj/ui';

import { EmptyState } from '~/components/EmptyState.tsx';
import { PageTitle } from '~/components/PageTitle.tsx';
import { Pager } from '~/components/Pager.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { useModerateReview, useReviews } from '~/lib/inbox-queries.ts';

type Review = components['schemas']['AdminReviewResponse'];

/*
 * "all" is a word rather than an empty string.
 *
 * The tab used to carry `''`, and `update()` deletes any parameter whose value is falsy — so
 * choosing "all" removed `?status=` from the URL and the page read its default straight back.
 * The tab was unreachable: it never highlighted and the list never changed. A named value is
 * kept in the URL like any other, and is translated to "no filter" at the query.
 */
/*
 * The API's own statuses. A rejected review is `Hidden` there — "rejected or taken down" — and is
 * called "Rejected" only on screen. The panel used to send and filter on `Rejected`, a status the
 * API does not have: rejecting answered 400, and the Rejected tab was ignored and listed everything.
 */
const TABS = ['Pending', 'Approved', 'Hidden', 'all'] as const;

/**
 * The moderation queue.
 *
 * A work list, not a table: the thing being judged is a paragraph of prose, and a review squeezed
 * into a table cell cannot be read — which is how a moderator ends up approving on the strength
 * of the star rating alone. Each one is a card with the whole review in it.
 *
 * Oldest first, from the API: the review that has been waiting longest is the one to deal with.
 */
export function ReviewsPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  // Pending by default. An empty inbox is the goal, and opening on "all" hides whether it is met.
  // `Rejected` was the old, wrong name for `Hidden`; a bookmark carrying it still lands on that tab.
  const requested = params.get('status') ?? 'Pending';
  const status = requested === 'Rejected' ? 'Hidden' : requested;
  const page = Number(params.get('page')) || 1;

  const reviews = useReviews({ status: status === 'all' ? '' : status, page });
  const moderate = useModerateReview();

  function update(changes: Record<string, string>) {
    const next = new URLSearchParams(params);

    for (const [key, value] of Object.entries(changes)) {
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    }

    if (!('page' in changes)) {
      next.delete('page');
    }

    setParams(next, { replace: true });
  }

  /**
   * Whether this button's own request is in flight. One mutation serves every card, so its
   * `isPending` alone put every Publish and Reject button on the page into a spinner at once.
   */
  function busyWith(review: Review, next: string): boolean {
    return (
      moderate.isPending && moderate.variables?.id === review.id && moderate.variables.body.status === next
    );
  }

  async function setStatus(review: Review, next: string) {
    try {
      await moderate.mutateAsync({ id: review.id, body: { status: next, moderationNote: null } });
      toast.show({
        title: next === 'Approved' ? t('reviews.published') : t('reviews.rejected'),
        tone: 'success',
      });
    } catch (error) {
      toast.show({
        title: t('reviews.couldNotModerate'),
        message: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'danger',
      });
    }
  }

  return (
    <>
      <PageTitle title={t('nav.reviews')} />

      {/*
        Tabs rather than a select: there are four states, staff switch between them constantly,
        and a dropdown hides which one is current behind a click.
      */}
      <nav className="section-nav" aria-label={t('reviews.filterByStatus')}>
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            className={`section-nav__item ${status === tab ? 'is-selected' : ''}`}
            aria-current={status === tab ? 'true' : undefined}
            onClick={() => update({ status: tab })}
          >
            {tab === 'all' ? t('reviews.all') : t(`reviewStatus.${tab}` as 'reviewStatus.Approved')}
          </button>
        ))}
      </nav>

      {reviews.isPending ? (
        <Skeleton height="320px" />
      ) : reviews.isError ? (
        <EmptyState
          icon="alert"
          title={t('common.somethingWentWrong')}
          hint={t('common.somethingWentWrongHint')}
          action={
            <button type="button" className="awj-button awj-button--secondary" onClick={() => reviews.refetch()}>
              {t('common.retry')}
            </button>
          }
        />
      ) : reviews.data.items.length === 0 ? (
        <EmptyState
          icon="star"
          title={status === 'Pending' ? t('reviews.inboxClear') : t('reviews.none')}
          hint={status === 'Pending' ? t('reviews.inboxClearHint') : undefined}
          tall
        />
      ) : (
        <>
          <ul className="inbox">
            {reviews.data.items.map((review) => (
              <li className="inbox__item card" key={review.id}>
                <div className="inbox__head">
                  <div>
                    <p className="inbox__title">
                      <Link className="data-table__link" to={`/products/${review.productId}`}>
                        <bdi>{review.productNameEn}</bdi>
                      </Link>
                    </p>
                    <p className="inbox__meta">
                      <bdi>{review.authorName}</bdi>
                      {' · '}
                      <bdi>{review.authorEmail}</bdi>
                      {' · '}
                      <bdi>{formatDate(locale, review.createdAt)}</bdi>
                      {review.isVerifiedPurchase ? (
                        <>
                          {' · '}
                          <span className="inbox__verified">{t('reviews.verified')}</span>
                        </>
                      ) : null}
                    </p>
                  </div>

                  <div className="inbox__headEnd">
                    <span
                      className="review-list__rating"
                      aria-label={t('customers.rating', { rating: String(review.rating) })}
                    >
                      <span aria-hidden="true">
                        {'★'.repeat(review.rating)}
                        {'☆'.repeat(5 - review.rating)}
                      </span>
                    </span>
                    <span className={`status status--${review.status.toLowerCase()}`}>
                      {t(`reviewStatus.${review.status}` as 'reviewStatus.Approved')}
                    </span>
                  </div>
                </div>

                {review.title ? (
                  <p className="inbox__subject">
                    <bdi>{review.title}</bdi>
                  </p>
                ) : null}

                <p className="inbox__body">
                  <bdi>{review.body}</bdi>
                </p>

                {review.reply ? (
                  <div className="inbox__reply">
                    <p className="inbox__replyLabel">{t('reviews.shopReplied')}</p>
                    <p>
                      <bdi>{review.reply}</bdi>
                    </p>
                  </div>
                ) : null}

                <div className="inbox__actions">
                  {review.status !== 'Approved' ? (
                    <Button
                      type="button"
                      size="sm"
                      loading={busyWith(review, 'Approved')}
                      loadingLabel={t('common.saving')}
                      onClick={() => setStatus(review, 'Approved')}
                    >
                      {t('reviews.publish')}
                    </Button>
                  ) : null}

                  {review.status !== 'Hidden' ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={busyWith(review, 'Hidden')}
                      loadingLabel={t('common.saving')}
                      onClick={() => setStatus(review, 'Hidden')}
                    >
                      {t('reviews.reject')}
                    </Button>
                  ) : null}

                  {/* The answer gets a page of its own; the list's tab and page travel with it,
                      so Back returns to this row rather than to the top of the queue. */}
                  <Link
                    className="awj-button awj-button--ghost awj-button--sm"
                    to={{ pathname: `/reviews/${review.id}/reply`, search: params.toString() }}
                  >
                    {review.reply ? t('reviews.editReply') : t('reviews.reply')}
                  </Link>
                </div>
              </li>
            ))}
          </ul>

          <Pager
            page={reviews.data.page}
            totalPages={reviews.data.totalPages ?? 1}
            total={reviews.data.total}
            onPage={(next) => update({ page: String(next) })}
          />
        </>
      )}

    </>
  );
}
